import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { canAccess, type ModuleKey } from '@/lib/permissions';

const menuItems: { icon: string; label: string; route: string; module: ModuleKey }[] = [
  { icon: 'ri-layout-grid-line', label: 'Dashboard', route: '/dashboard', module: 'dashboard' },
  { icon: 'ri-team-line', label: 'Team & Counselors', route: '/dashboard/team', module: 'team' },
  { icon: 'ri-briefcase-4-line', label: 'Staff HR', route: '/dashboard/hr', module: 'hr' },
  { icon: 'ri-money-rupee-circle-line', label: 'Payroll', route: '/dashboard/payroll', module: 'payroll' },
  { icon: 'ri-graduation-cap-line', label: 'Students', route: '/dashboard/students', module: 'students' },
  { icon: 'ri-building-4-line', label: 'Universities & Courses', route: '/dashboard/institutions', module: 'institutions' },
  { icon: 'ri-file-list-3-line', label: 'Applications', route: '/dashboard/applications', module: 'applications' },
  { icon: 'ri-passport-line', label: 'Visa & Pre-Departure', route: '/dashboard/visa', module: 'visa' },
  { icon: 'ri-folder-shield-2-line', label: 'Documents', route: '/dashboard/documents', module: 'documents' },
  { icon: 'ri-wallet-3-line', label: 'Finance', route: '/dashboard/finance', module: 'finance' },
  { icon: 'ri-focus-3-line', label: 'Leads', route: '/dashboard/leads', module: 'leads' },
  { icon: 'ri-calendar-check-line', label: 'Appointments', route: '/dashboard/appointments', module: 'appointments' },
  { icon: 'ri-task-line', label: 'Tasks & Communications', route: '/dashboard/tasks', module: 'tasks' },
  { icon: 'ri-pulse-line', label: 'Activity Logs', route: '/dashboard/activity', module: 'activity' },
  { icon: 'ri-bar-chart-2-line', label: 'Reports', route: '/dashboard/reports', module: 'reports' },
  { icon: 'ri-bank-card-line', label: 'Subscription', route: '/dashboard/subscription', module: 'subscription' },
  { icon: 'ri-settings-3-line', label: 'Settings', route: '/dashboard/settings', module: 'settings' },
];

interface DashboardSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function DashboardSidebar({ collapsed, onToggle }: DashboardSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();
  const visibleItems = menuItems.filter((item) => canAccess(profile?.role, item.module));

  const isActive = (route: string) => {
    if (route === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(route);
  };

  return (
    <aside
      className={`fixed left-0 top-0 h-full bg-[#0E1B2E] flex flex-col z-40 transition-all duration-300 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo */}
      <div className={`flex items-center h-16 border-b border-white/10 px-4 ${collapsed ? 'justify-center' : 'justify-between'}`}>
        {!collapsed && (
          <img src="/assets/logo2.png" alt="EzyAbroad" className="h-8 w-auto" />
        )}
        <button
          onClick={onToggle}
          className="w-8 h-8 flex items-center justify-center rounded-md text-white/60 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
        >
          <i className={`${collapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'} text-lg`} />
        </button>
      </div>

      {/* Nav Items */}
      <nav className="flex-1 overflow-y-auto no-scrollbar py-4 space-y-0.5 px-2">
        {visibleItems.map((item) => {
          const active = isActive(item.route);
          return (
            <button
              key={item.route}
              onClick={() => navigate(item.route)}
              title={collapsed ? item.label : undefined}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap ${
                active
                  ? 'bg-[#0FA3A5] text-[#0E1B2E] font-semibold'
                  : 'text-[#B0BEC5] font-medium hover:text-white hover:bg-[#162942]'
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-base`} />
              </div>
              {!collapsed && <span>{item.label}</span>}
            </button>
          );
        })}
      </nav>

    </aside>
  );
}
